import React, { useMemo } from "react";
import { ArrowUp, ArrowDown } from "lucide-react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Row } from "primereact/row";
import { ColumnGroup } from "primereact/columngroup";
import TrueIqIcon from "app/assets/IconComponent/TrueIqIcon";
import { Avatar } from "primereact/avatar";
import TruncatedText from "app/uicomponents/microcomponents/TruncatedText";
import AssetValueChartFilter from "v2/features/plans/components/AssetValueChartFilter";
import { formatToCompactNumber } from "app/utils/utils";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface PerformanceLedgerRow {
  id: string;
  shareholderName: string;
  avatarUrl?: string;
  initials: string;
  avatarColor: string;
  ownership: number;
  value: number;
  trueIqScore?: number;
  life: {
    coverage: number;
    status: "SURPLUS" | "DEFICIT";
    difference: number;
    trueIqIndex?: number;
  };
  disability: {
    coverage: number;
    status: "SURPLUS" | "DEFICIT";
    difference: number;
    trueIqIndex?: number;
  };
}

export interface LedgerYearInfo {
  planYear: number;
  calendarYear?: number;
  insuredAge?: number;
}

export interface LedgerColumnSummary {
  lifeCoverage: number;
  lifeStatus: "SURPLUS" | "DEFICIT";
  lifeDiff: number;
  lifeTrueIqIndex?: number;
  disabilityCoverage: number;
  disabilityStatus: "SURPLUS" | "DEFICIT";
  disabilityDiff: number;
  disabilityTrueIqIndex?: number;
}

export interface LedgerProjectionColumn {
  rows: PerformanceLedgerRow[];
  yearInfo: LedgerYearInfo;
  /** Completed projection years render solid instead of dashed. */
  completed?: boolean;
  summary?: LedgerColumnSummary;
}

interface PerformanceLedgerProps {
  rows?: PerformanceLedgerRow[];
  projectionColumns?: LedgerProjectionColumn[];
  selectedYear?: number;
  /** Year metadata for the current column — drives the top year bar label. */
  currentYearInfo?: LedgerYearInfo;
  /** Coverage totals for the current column footer (from API `coverage` block). */
  currentSummary?: LedgerColumnSummary;
  isDark?: boolean;
  showProtect?: boolean;
  showProjections?: boolean;
  /** Label for the first (person) column. "Shareholder" for Buy Sell, "Key Person" for KP. */
  personLabel?: string;
  /** Label for the value column. "Value" for Buy Sell, "Exposure Value" for KP. */
  valueLabel?: string;
  /** Whether to render the Ownership column. Key Person plans have no ownership %, so hide it. */
  showOwnership?: boolean;
  /** Date-toggle (Year / Plan Year / Insured Age) shared with the Performance Chart. */
  chartFilter?: string;
  onChartFilterChange?: (v: string) => void;
  enableAgeFilter?: boolean;
}

interface YearSlice {
  key: string;
  yearInfo: LedgerYearInfo;
  completed: boolean;
  isCurrent: boolean;
  summary: LedgerColumnSummary;
}

type LedgerTableRow = {
  id: string;
  shareholder: PerformanceLedgerRow;
  [field: string]: unknown;
};

// ── Helpers ───────────────────────────────────────────────────────────────────

const HEADER_CLS = "text-[11px] font-semibold uppercase tracking-wider text-gray-400";

const formatLedgerYearLabel = (chartFilter: string, info: LedgerYearInfo): string => {
  if (chartFilter === "insuredAge" && info.insuredAge != null) return `A${info.insuredAge}`;
  if (chartFilter === "planYear") return `YR${info.planYear}`;
  if (info.calendarYear != null) return `${info.calendarYear}`;
  return `YR${info.planYear}`;
};

const summarizeRows = (rows: PerformanceLedgerRow[]): LedgerColumnSummary => {
  const lifeCoverage = rows.reduce((s, r) => s + r.life.coverage, 0);
  const disabilityCoverage = rows.reduce((s, r) => s + r.disability.coverage, 0);
  const lifeNet = rows.reduce(
    (s, r) => s + (r.life.status === "SURPLUS" ? r.life.difference : -r.life.difference),
    0,
  );
  const disabilityNet = rows.reduce(
    (s, r) =>
      s + (r.disability.status === "SURPLUS" ? r.disability.difference : -r.disability.difference),
    0,
  );
  const lifeIqRows = rows;
  const disabilityIqRows = rows;
  return {
    lifeCoverage,
    lifeStatus: lifeNet >= 0 ? "SURPLUS" : "DEFICIT",
    lifeDiff: Math.abs(lifeNet),
    disabilityCoverage,
    disabilityStatus: disabilityNet >= 0 ? "SURPLUS" : "DEFICIT",
    disabilityDiff: Math.abs(disabilityNet),
    lifeTrueIqIndex:
      lifeIqRows.length > 0
        ? Math.round(
            lifeIqRows.reduce((s, r) => s + (r.life.trueIqIndex ?? 0), 0) / lifeIqRows.length,
          )
        : 0,
    disabilityTrueIqIndex:
      disabilityIqRows.length > 0
        ? Math.round(
            disabilityIqRows.reduce((s, r) => s + (r.disability.trueIqIndex ?? 0), 0) /
              disabilityIqRows.length,
          )
        : 0,
  };
};

const projectionBg = (completed: boolean) => (completed ? "transparent" : "#EEF6FF");

const YEAR_GROUP_GAP = 24;

const yearGroupBg = (slice: YearSlice, stickyBg: string) =>
  slice.isCurrent ? stickyBg : projectionBg(slice.completed);

/** Dashed projection columns use a light background in both themes. */
const isLightProjectionSlice = (slice: YearSlice) => !slice.isCurrent && !slice.completed;

const metricHeaderCls = (slice: YearSlice) =>
  isLightProjectionSlice(slice)
    ? "text-[11px] font-semibold uppercase tracking-wider text-[#55556D]"
    : HEADER_CLS;

const metricValueTextCls = (slice: YearSlice, isDark: boolean) =>
  isLightProjectionSlice(slice) ? "text-[#191A1C]" : isDark ? "text-gray-200" : "text-[#191A1C]";

const metricSubTextCls = (slice: YearSlice, isDark: boolean) =>
  isLightProjectionSlice(slice) ? "text-[#55556D]" : isDark ? "text-gray-400" : "text-[#55556D]";

const CHIP_WIDTH_PX = 41;
const CHIP_GAP_PX = 6;

const yearColStyle = (
  slice: YearSlice,
  yearIdx: number,
  stickyBg: string,
  isFirst: boolean,
  isLast: boolean,
  reserveChipSpace = false,
): React.CSSProperties => ({
  background: yearGroupBg(slice, stickyBg),
  paddingLeft: isFirst ? (yearIdx > 0 ? YEAR_GROUP_GAP + 8 : 12) : 12,
  paddingRight: reserveChipSpace
    ? isLast
      ? CHIP_WIDTH_PX + CHIP_GAP_PX + 12
      : CHIP_WIDTH_PX + CHIP_GAP_PX
    : isLast
    ? 24
    : 12,
});

const yearBarColStyle = (yearIdx: number, totalYears: number, stickyBg: string) => ({
  paddingBottom: 8,
  paddingTop: 0,
  paddingLeft: yearIdx > 0 ? YEAR_GROUP_GAP : 0,
  paddingRight: yearIdx < totalYears - 1 ? 12 : 0,
  verticalAlign: "bottom" as const,
  background: stickyBg,
});

const YearBar = ({
  label,
  dashed,
  completed,
}: {
  label: string;
  dashed: boolean;
  completed: boolean;
}) => {
  const solid = !dashed || completed;
  const barStyle = solid
    ? {
        backgroundColor: "#004781",
        borderRadius: 6,
        padding: "9px 0",
        textAlign: "center" as const,
      }
    : {
        border: "2px dashed #3B82F6",
        borderRadius: 8,
        padding: "9px 0",
        textAlign: "center" as const,
        backgroundColor: "#EEF6FF",
      };
  return (
    <div
      className="ledger-year-bar"
      style={{ width: "100%", boxSizing: "border-box", ...barStyle }}
    >
      <span
        className="conthrax-font"
        style={{
          color: solid ? "white" : "#3B82F6",
          fontWeight: 700,
          fontSize: 16,
          letterSpacing: "0.12em",
        }}
      >
        {label}
      </span>
    </div>
  );
};

const RowBadge = ({
  coverage,
  status,
  difference,
  trueIqIndex,
}: {
  coverage: number;
  status: "SURPLUS" | "DEFICIT";
  difference: number;
  trueIqIndex: number;
}) => {
  const isGreen = status === "SURPLUS";
  const main = isGreen ? "#1DA750" : "#E24C4C";
  const bg = isGreen ? "#E4F8F0" : "#FFE7E6";
  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "2px 0" }}>
      <div style={{ position: "relative", display: "inline-block", maxWidth: "100%" }}>
        <div
          style={{
            backgroundColor: bg,
            border: `1px solid ${main}`,
            borderRadius: 6,
            padding: "4px 8px",
            minWidth: 146,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <p
            style={{
              color: main,
              fontWeight: 700,
              fontSize: 16,
              lineHeight: "21px",
              margin: 0,
              textAlign: "center",
            }}
          >
            {formatToCompactNumber(coverage, { prefix: "$" })}
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
            <div
              style={{
                width: 18,
                height: 18,
                borderRadius: "50%",
                backgroundColor: main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {isGreen ? (
                <ArrowUp size={10} color="white" strokeWidth={3} />
              ) : (
                <ArrowDown size={10} color="white" strokeWidth={3} />
              )}
            </div>
            <span style={{ color: main, fontSize: 12, lineHeight: "21px", whiteSpace: "nowrap" }}>
              <span style={{ fontWeight: 600 }}>{status}</span>{" "}
              {formatToCompactNumber(difference, { prefix: "$" })}
            </span>
          </div>
        </div>
        <span
          style={{
            position: "absolute",
            top: "50%",
            left: "100%",
            // 20% of chip width overlaps the card; 80% sits outside (fixed 41px width).
            transform: "translate(-20%, -50%)",
            backgroundColor: "#ffffff",
            border: `1px solid ${main}`,
            color: main,
            fontSize: 12,
            fontWeight: 600,
            padding: "2px 0",
            borderRadius: 6,
            whiteSpace: "nowrap",
            zIndex: 1,
            textAlign: "center",
            width: CHIP_WIDTH_PX,
            boxSizing: "border-box",
          }}
        >
          {trueIqIndex}%
        </span>
      </div>
    </div>
  );
};

const FOOTER_ICON_SIZE = 38;
const FOOTER_ARROW_SIZE = 25;

const SummaryBadge = ({
  status,
  amount,
  trueIqIndex,
}: {
  status: "SURPLUS" | "DEFICIT";
  amount: number;
  trueIqIndex: number;
}) => {
  const isGreen = status === "SURPLUS";
  const main = isGreen ? "#1DA750" : "#E24C4C";
  const bg = isGreen ? "#E4F8F0" : "#FFE7E6";
  return (
    <div style={{ display: "inline-block", paddingBottom: 14 }}>
      <div style={{ position: "relative", display: "inline-block" }}>
        <div
          style={{
            backgroundColor: bg,
            border: `1px solid ${main}`,
            borderRadius: 6,
            padding: "6px 10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            minWidth: 148,
          }}
        >
          <div
            style={{
              width: FOOTER_ICON_SIZE,
              height: FOOTER_ICON_SIZE,
              borderRadius: "50%",
              backgroundColor: main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {isGreen ? (
              <ArrowUp size={FOOTER_ARROW_SIZE} color="#ffffff" strokeWidth={3} />
            ) : (
              <ArrowDown size={FOOTER_ARROW_SIZE} color="#ffffff" strokeWidth={3} />
            )}
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            <span
              className="conthrax-font"
              style={{ color: main, fontSize: 13, lineHeight: "16px", fontWeight: 400 }}
            >
              {status}
            </span>
            <span style={{ color: main, fontSize: 16, fontWeight: 700, lineHeight: "18px" }}>
              {formatToCompactNumber(amount, { prefix: "$" })}
            </span>
          </div>
        </div>
        <span
          style={{
            position: "absolute",
            left: "50%",
            bottom: 0,
            // 20% of chip height overlaps the card; 80% sits below (fixed 41px width).
            transform: "translate(-50%, 80%)",
            backgroundColor: "#ffffff",
            border: `1px solid ${main}`,
            color: main,
            fontSize: 12,
            fontWeight: 600,
            padding: "2px 0",
            borderRadius: 6,
            whiteSpace: "nowrap",
            zIndex: 1,
            textAlign: "center",
            width: CHIP_WIDTH_PX,
            boxSizing: "border-box",
          }}
        >
          {trueIqIndex}%
        </span>
      </div>
    </div>
  );
};

const COVERAGE_LABEL_STYLE: React.CSSProperties = {
  fontFamily: "Inter, sans-serif",
  fontSize: 16,
  fontWeight: 600,
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#004781",
  textAlign: "center",
};

const CoverageFooter = ({ summary }: { summary: LedgerColumnSummary }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
      width: "100%",
    }}
  >
    <span style={COVERAGE_LABEL_STYLE}>
      Coverage: {formatToCompactNumber(summary.lifeCoverage, { prefix: "$" })}
    </span>
    <SummaryBadge
      status={summary.lifeStatus}
      amount={summary.lifeDiff}
      trueIqIndex={summary.lifeTrueIqIndex ?? 0}
    />
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

export default function PerformanceLedger({
  rows,
  projectionColumns = [],
  selectedYear = 1,
  currentYearInfo,
  currentSummary,
  isDark = false,
  showProtect = false,
  showProjections = false,
  personLabel = "Shareholder",
  valueLabel = "Value",
  showOwnership = true,
  chartFilter = "year",
  onChartFilterChange,
  enableAgeFilter = false,
}: PerformanceLedgerProps) {
  const data = rows ?? [];
  const visibleProjections = showProjections ? projectionColumns : [];
  const metricColSpan = showOwnership ? 4 : 3;

  const summary = useMemo(() => currentSummary ?? summarizeRows(data), [currentSummary, data]);

  const yearSlices = useMemo<YearSlice[]>(() => {
    const current: YearSlice = {
      key: `y${currentYearInfo?.planYear ?? selectedYear}`,
      yearInfo: currentYearInfo ?? { planYear: selectedYear },
      completed: true,
      isCurrent: true,
      summary,
    };
    const projections: YearSlice[] = visibleProjections.map((col) => ({
      key: `y${col.yearInfo.planYear}`,
      yearInfo: col.yearInfo,
      completed: col.completed === true,
      isCurrent: false,
      summary: col.summary ?? summarizeRows(col.rows),
    }));
    return [current, ...projections];
  }, [currentYearInfo, selectedYear, summary, visibleProjections]);

  const tableData = useMemo<LedgerTableRow[]>(
    () =>
      data.map((row, i) => {
        const flat: LedgerTableRow = { id: row.id, shareholder: row };
        yearSlices.forEach((slice, sliceIdx) => {
          const src = sliceIdx === 0 ? row : visibleProjections[sliceIdx - 1]?.rows[i] ?? row;
          flat[`${slice.key}_ownership`] = src.ownership;
          flat[`${slice.key}_value`] = src.value;
          flat[`${slice.key}_life`] = src.life;
          flat[`${slice.key}_disability`] = src.disability;
        });
        return flat;
      }),
    [data, yearSlices, visibleProjections],
  );

  const stickyBg = isDark ? "#2A2A3D" : "#ffffff";
  const valueColMinWidth = showOwnership ? 96 : 140;
  const yearGroupMinWidth = showOwnership ? 520 : valueColMinWidth + 180 + 180;
  const tableMinWidth =
    180 + yearSlices.length * yearGroupMinWidth + (yearSlices.length - 1) * YEAR_GROUP_GAP;

  const metricHeaderColumns = yearSlices.flatMap((slice, yearIdx) => {
    const headerPad = { paddingBottom: 10, paddingTop: 4 };
    const cols = [];
    if (showOwnership) {
      cols.push(
        <Column
          key={`hdr-${slice.key}-ownership`}
          field={`${slice.key}_ownership`}
          align="center"
          header={<span className={metricHeaderCls(slice)}>Ownership</span>}
          style={{
            minWidth: 96,
            ...yearColStyle(slice, yearIdx, stickyBg, true, false),
            ...headerPad,
          }}
        />,
      );
    }
    cols.push(
      <Column
        key={`hdr-${slice.key}-value`}
        field={`${slice.key}_value`}
        align="center"
        header={
          <span className={metricHeaderCls(slice)} style={{ whiteSpace: "nowrap" }}>
            {valueLabel}
          </span>
        }
        style={{
          minWidth: valueColMinWidth,
          ...yearColStyle(slice, yearIdx, stickyBg, !showOwnership, false),
          ...headerPad,
        }}
      />,
      <Column
        key={`hdr-${slice.key}-life`}
        field={`${slice.key}_life`}
        align="center"
        header={<span className={metricHeaderCls(slice)}>Life</span>}
        style={{
          minWidth: 180,
          ...yearColStyle(slice, yearIdx, stickyBg, false, false),
          ...headerPad,
        }}
      />,
      <Column
        key={`hdr-${slice.key}-disability`}
        field={`${slice.key}_disability`}
        align="center"
        header={<span className={metricHeaderCls(slice)}>Disability</span>}
        style={{
          minWidth: 180,
          ...yearColStyle(slice, yearIdx, stickyBg, false, true),
          ...headerPad,
        }}
      />,
    );
    return cols;
  });

  const metricBodyColumns = yearSlices.flatMap((slice, yearIdx) => {
    const cellBg = yearGroupBg(slice, stickyBg);

    const cols = [];
    if (showOwnership) {
      cols.push(
        <Column
          key={`${slice.key}_ownership`}
          field={`${slice.key}_ownership`}
          header="Ownership"
          align="center"
          body={(rowData: LedgerTableRow) => (
            <span className={`font-semibold text-[13px] ${metricSubTextCls(slice, isDark)}`}>
              {rowData[`${slice.key}_ownership`] as number}%
            </span>
          )}
          footer={<span />}
          footerStyle={{ background: cellBg }}
          style={yearColStyle(slice, yearIdx, stickyBg, true, false)}
        />,
      );
    }
    cols.push(
      <Column
        key={`${slice.key}_value`}
        field={`${slice.key}_value`}
        header={valueLabel}
        align="center"
        body={(rowData: LedgerTableRow) => (
          <span className={`font-semibold text-[13px] ${metricValueTextCls(slice, isDark)}`}>
            {formatToCompactNumber(rowData[`${slice.key}_value`] as number, { prefix: "$" })}
          </span>
        )}
        footer={<span />}
        footerStyle={{ background: cellBg }}
        style={{
          minWidth: valueColMinWidth,
          ...yearColStyle(slice, yearIdx, stickyBg, !showOwnership, false),
        }}
      />,
      <Column
        key={`${slice.key}_life`}
        field={`${slice.key}_life`}
        header="Life"
        align="center"
        body={(rowData: LedgerTableRow) => {
          const life = rowData[`${slice.key}_life`] as PerformanceLedgerRow["life"];
          return (
            <RowBadge
              coverage={life.coverage}
              status={life.status}
              difference={life.difference}
              trueIqIndex={life.trueIqIndex ?? 0}
            />
          );
        }}
        footer={<CoverageFooter summary={slice.summary} />}
        footerStyle={{ background: cellBg, paddingBottom: "1.25rem", paddingTop: "0.5rem" }}
        style={yearColStyle(slice, yearIdx, stickyBg, false, false, true)}
      />,
      <Column
        key={`${slice.key}_disability`}
        field={`${slice.key}_disability`}
        header="Disability"
        align="center"
        body={(rowData: LedgerTableRow) => {
          const disability = rowData[
            `${slice.key}_disability`
          ] as PerformanceLedgerRow["disability"];
          return (
            <RowBadge
              coverage={disability.coverage}
              status={disability.status}
              difference={disability.difference}
              trueIqIndex={disability.trueIqIndex ?? 0}
            />
          );
        }}
        footer={
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 6,
              width: "100%",
            }}
          >
            <span style={COVERAGE_LABEL_STYLE}>
              Coverage: {formatToCompactNumber(slice.summary.disabilityCoverage, { prefix: "$" })}
            </span>
            <SummaryBadge
              status={slice.summary.disabilityStatus}
              amount={slice.summary.disabilityDiff}
              trueIqIndex={slice.summary.disabilityTrueIqIndex ?? 0}
            />
          </div>
        }
        footerStyle={{ background: cellBg, paddingBottom: "1.25rem", paddingTop: "0.5rem" }}
        style={yearColStyle(slice, yearIdx, stickyBg, false, true, true)}
      />,
    );
    return cols;
  });

  const headerColumnGroup = (
    <ColumnGroup>
      <Row>
        <Column
          header=""
          style={{
            minWidth: 180,
            background: stickyBg,
            verticalAlign: "bottom",
            paddingBottom: 8,
          }}
        />
        {yearSlices.map((slice, yearIdx) => (
          <Column
            key={`year-bar-${slice.key}`}
            header={
              <YearBar
                label={formatLedgerYearLabel(chartFilter, slice.yearInfo)}
                dashed={!slice.isCurrent}
                completed={slice.completed}
              />
            }
            colSpan={metricColSpan}
            style={yearBarColStyle(yearIdx, yearSlices.length, stickyBg)}
          />
        ))}
      </Row>
      <Row>
        <Column
          field="shareholder"
          align="left"
          header={<span className={HEADER_CLS}>{personLabel}</span>}
          frozen
          style={{
            minWidth: 180,
            background: stickyBg,
            paddingLeft: 16,
            paddingRight: 16,
            paddingBottom: 10,
            paddingTop: 4,
          }}
        />
        {metricHeaderColumns}
      </Row>
    </ColumnGroup>
  );

  const shareholderBody = (rowData: LedgerTableRow) => {
    const row = rowData.shareholder;
    return (
      <div className="flex items-center gap-2 w-fit">
        <Avatar
          shape="circle"
          className="w-[36px] h-[36px]"
          image={row.avatarUrl}
          label={row.avatarUrl ? undefined : row.initials}
          style={
            row.avatarUrl
              ? undefined
              : { backgroundColor: row.avatarColor, color: "#ffffff", fontSize: 13 }
          }
        />
        <div className="flex flex-col">
          <TruncatedText
            text={row.shareholderName || "-"}
            className="text-[14px] font-normal leading-none"
            maxWidth="max-w-full"
          />
          {showProtect && row.trueIqScore != null && (
            <div className="flex items-center gap-1">
              <TrueIqIcon
                width={13}
                height={13}
                fill={row.trueIqScore >= 100 ? "#22C55E" : "#EF4444"}
              />
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: row.trueIqScore >= 100 ? "#22C55E" : "#EF4444",
                }}
              >
                {row.trueIqScore.toFixed(1)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={isDark ? "bg-[#2A2A3D]" : "bg-white"}>
      <div className="performance-ledger-table-root w-full min-w-0 overflow-hidden">
        <DataTable
          value={tableData}
          scrollable
          headerColumnGroup={headerColumnGroup}
          showGridlines={false}
          className="performance-ledger-table"
          tableStyle={{ minWidth: tableMinWidth, width: "100%" }}
        >
          <Column
            field="shareholder"
            header={personLabel}
            frozen
            align="left"
            style={{ minWidth: 180, background: stickyBg, paddingLeft: 16, paddingRight: 16 }}
            body={shareholderBody}
            footer={
              <AssetValueChartFilter
                selectedChartFilter={chartFilter}
                onChartFilterChange={onChartFilterChange ?? (() => {})}
                showProjections={false}
                onProjectionsToggle={() => {}}
                enableAgeFilter={enableAgeFilter}
                hideProjection
                overrideCalendarIcon
              />
            }
          />
          {metricBodyColumns}
        </DataTable>
        <style>
          {`
          .performance-ledger-table-root .performance-ledger-table .p-datatable-wrapper {
            overflow-x: auto;
          }
          .performance-ledger-table-root .performance-ledger-table .p-datatable-scrollable-body-table,
          .performance-ledger-table-root .performance-ledger-table .p-datatable-scrollable-header-table,
          .performance-ledger-table-root .performance-ledger-table .p-datatable-scrollable-footer-table {
            min-width: ${tableMinWidth}px !important;
          }
          .performance-ledger-table-root .performance-ledger-table .p-datatable-thead > tr:first-child > th {
            border: none !important;
            padding-top: 0 !important;
            vertical-align: bottom;
          }
          .performance-ledger-table-root .performance-ledger-table .p-datatable-thead > tr:nth-child(2) > th {
            border: none !important;
            border-bottom: 1px solid ${isDark ? "#3A3A50" : "#F3F4F6"} !important;
            vertical-align: bottom !important;
          }
          .performance-ledger-table-root .performance-ledger-table .p-datatable-tbody > tr > td {
            border: none !important;
            border-bottom: 1px solid ${isDark ? "#3A3A50" : "#F3F4F6"} !important;
            padding-top: 0.5rem !important;
            padding-bottom: 0.5rem !important;
            vertical-align: middle;
            overflow: visible !important;
          }
          .performance-ledger-table-root .performance-ledger-table .p-datatable-tbody > tr:last-child > td {
            border-bottom: none !important;
          }
          .performance-ledger-table-root .performance-ledger-table .p-datatable-tfoot > tr > td {
            border: none !important;
            border-top: 1px solid ${isDark ? "#3A3A50" : "#F3F4F6"} !important;
            padding: 0.75rem !important;
            vertical-align: top;
            overflow: visible !important;
          }
          .performance-ledger-table-root .performance-ledger-table .p-datatable-thead > tr > th.p-frozen-column,
          .performance-ledger-table-root .performance-ledger-table .p-datatable-tbody > tr > td.p-frozen-column,
          .performance-ledger-table-root .performance-ledger-table .p-datatable-tfoot > tr > td.p-frozen-column {
            position: sticky !important;
            left: 0 !important;
            z-index: 2 !important;
            background: ${stickyBg} !important;
            box-shadow: ${
              isDark ? "4px 0 8px -4px rgba(0,0,0,0.35)" : "4px 0 8px -4px rgba(0,0,0,0.08)"
            };
          }
          .performance-ledger-table-root .performance-ledger-table .p-datatable-thead > tr > th.p-frozen-column {
            z-index: 3 !important;
          }
          .performance-ledger-table-root .performance-ledger-table .p-datatable-tfoot > tr > td.p-frozen-column {
            z-index: 3 !important;
          }
          .performance-ledger-table-root .performance-ledger-table .p-datatable-thead > tr:nth-child(2) > th.p-frozen-column .p-column-header-content {
            justify-content: flex-start !important;
          }
          .performance-ledger-table-root
            .performance-ledger-table
            .p-datatable-thead
            > tr:nth-child(2)
            > th:not(.p-frozen-column)
            .p-column-header-content {
            justify-content: center !important;
          }
          .performance-ledger-table-root
            .performance-ledger-table
            .p-datatable-thead
            > tr:first-child
            > th
            .p-column-header-content {
            display: block;
            width: 100%;
          }
          .performance-ledger-table-root .performance-ledger-table .ledger-year-bar {
            width: 100%;
          }
          .performance-ledger-table-root .performance-ledger-table .p-datatable-tbody > tr > td.p-align-center,
          .performance-ledger-table-root .performance-ledger-table .p-datatable-tfoot > tr > td.p-align-center {
            text-align: center !important;
          }
        `}
        </style>
      </div>
    </div>
  );
}
